import { replace } from 'lodash/fp';

import { CzechBankAccount, PartyAccount } from '@skippay/core/@types/bank';
import { COUNTRY_CODE_CZ } from '@skippay/core/constants/countryCodes';

import { Maybe } from './types';

export const toDigits = (value: string): string =>
	replace(/[A-Z]/g, (char) => String(char.charCodeAt(0) - 55))(value);

const zeroPad = (length: number, value: string) => value.padStart(length, '0');

export const formatIBAN = (iban: string): string => iban.match(/.{1,4}/g)?.join(' ') || iban;

export const czechBankAccountToIBAN = (
	{ bankCode, firstPart, secondPart }: CzechBankAccount,
	addSpaceSeparators?: boolean
): string => {
	const countryCode = COUNTRY_CODE_CZ;
	const bankNumber = `${bankCode}${zeroPad(6, firstPart || '')}${zeroPad(10, secondPart)}`;

	const checksum = 98 - Number(BigInt(toDigits(`${bankNumber}${countryCode}00`)) % BigInt(97));

	const iban = `${COUNTRY_CODE_CZ}${zeroPad(2, String(checksum))}${bankNumber}`;

	if (addSpaceSeparators) {
		return formatIBAN(iban);
	}

	return iban;
};

export const IBAN_RE = /^([A-Z]{2})(\d{2})([A-Z\d]{11,30})$/;

export const IBANToCzechBankAccount = (iban: string): CzechBankAccount => {
	const match = iban.match(IBAN_RE);
	if (!match) {
		throw new Error(`Invalid IBAN: "${iban}"`);
	}
	const [, countryCode, , bankNumber] = match;
	if (countryCode !== COUNTRY_CODE_CZ) {
		throw new Error(
			`Only czech account could be converted to czech domestic bank account identifier`
		);
	}
	const bankCode = bankNumber.slice(0, 4);
	const firstPart = Number(bankNumber.slice(4, 10));
	const secondPart = Number(bankNumber.slice(10));
	return firstPart
		? { bankCode, firstPart: String(firstPart), secondPart: String(secondPart) }
		: { bankCode, secondPart: String(secondPart) };
};

/**
 * Utilities for the final-form - stringifies the CzechBankAccount
 */
export const formatCzechBankAccount = (czechBankAccount: Maybe<CzechBankAccount>): Maybe<string> =>
	czechBankAccount &&
	`${[czechBankAccount.firstPart, czechBankAccount.secondPart].filter(Boolean).join('-')}/${
		czechBankAccount.bankCode
	}`;

const czechBankAccountRe = /^(?:(\d+)-)?(\d+)\/(\d+)$/;
/**
 * Utilities for the final-form - parses the CzechBankAccount
 */
export const parseCzechBankAccount = (
	czechBankAccount: Maybe<string>
): Maybe<string> | CzechBankAccount => {
	const match = czechBankAccount?.match(czechBankAccountRe);
	if (!match) {
		return czechBankAccount;
	}
	const [, firstPart, secondPart, bankCode] = match;
	return { bankCode, firstPart, secondPart };
};

function isValidIBANChecksum(iban: string): boolean {
	const rearranged = iban.slice(4) + iban.slice(0, 4);

	const numericIban = rearranged
		.split('')
		.map((char) => (Number.isNaN(Number(char)) ? char.charCodeAt(0) - 55 : char))
		.join('');

	return BigInt(numericIban) % 97n === 1n;
}

export function isValidIBANInPartyAccount(partyAccount: PartyAccount): boolean {
	const { iban } = partyAccount;

	if (!iban || !IBAN_RE.test(iban)) {
		return false;
	}

	return isValidIBANChecksum(iban);
}
